/**
 * Registrar Gasto - Backoffice Web Chofer
 * Path: backoffice-react/src/pages/chofer/NuevoGastoChofer.jsx
 * Última modificación: 2026-01-03 18:10
 * Dev: cpalacios01@gmail.com | +595994648273
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Alert,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  InputAdornment,
} from '@mui/material';
import {
  LocalGasStation as GasIcon,
  Build as RepairIcon,
  TollOutlined as TollIcon,
  Restaurant as FoodIcon,
  MoreHoriz as OtherIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const conceptos = [
  { value: 'combustible', label: 'Combustible', icon: <GasIcon /> },
  { value: 'reparacion', label: 'Reparación', icon: <RepairIcon /> },
  { value: 'peaje', label: 'Peaje', icon: <TollIcon /> },
  { value: 'comida', label: 'Comida', icon: <FoodIcon /> },
  { value: 'otros', label: 'Otros', icon: <OtherIcon /> },
];

const NuevoGastoChofer = () => {
  const navigate = useNavigate();
  const [redondo, setRedondo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const [formData, setFormData] = useState({
    concepto: '',
    monto: '',
    comprobante: '',
    descripcion: '',
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    cargarRedondo();
  }, []);

  const cargarRedondo = async () => {
    try {
      const response = await axios.get('/api/redondos/activo');
      setRedondo(response.data);
    } catch (err) {
      setError('No tienes un redondo activo');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });
    
    // Limpiar error del campo
    if (errors[field]) {
      setErrors({
        ...errors,
        [field]: null,
      });
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.concepto) {
      newErrors.concepto = 'Debes seleccionar un concepto';
    }

    if (!formData.monto || parseFloat(formData.monto) <= 0) {
      newErrors.monto = 'El monto debe ser mayor a 0';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setSubmitting(true);
    setError(null);

    try {
      await axios.post('/api/gastos', {
        redondo_id: redondo.id,
        concepto: formData.concepto,
        monto: parseFloat(formData.monto),
        comprobante: formData.comprobante || null,
        descripcion: formData.descripcion || null,
        fecha_hora: new Date().toISOString(),
      });

      setSuccess(true);
      
      setTimeout(() => {
        navigate('/chofer/dashboard');
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.message || 'Error al registrar gasto');
      setSubmitting(false);
    }
  };

  const formatMoney = (amount) => {
    if (!amount) return '';
    return new Intl.NumberFormat('es-PY').format(amount);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!redondo) {
    return (
      <Alert severity="warning">
        No tienes un redondo activo. Contacta al rendidor.
      </Alert>
    );
  }

  if (success) {
    return (
      <Box textAlign="center" py={6}>
        <CheckIcon sx={{ fontSize: 100, color: '#FF5722', mb: 3 }} />
        <Typography variant="h4" fontWeight="bold" color="success.main" gutterBottom>
          ¡Gasto Registrado!
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Redirigiendo...
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Card sx={{ mb: 3, bgcolor: '#FF5722', color: 'white' }}>
        <CardContent>
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            Registrar Nuevo Gasto
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                Redondo
              </Typography>
              <Typography variant="h6" fontWeight="bold">
                {redondo.codigo_unico}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                Móvil
              </Typography>
              <Typography variant="h6" fontWeight="bold">
                {redondo.bus?.numero}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Formulario */}
      <Card>
        <CardContent>
          <Grid container spacing={3}>
            
            {/* Concepto */}
            <Grid item xs={12}>
              <FormControl fullWidth error={!!errors.concepto}>
                <InputLabel>Concepto del Gasto *</InputLabel>
                <Select
                  value={formData.concepto}
                  onChange={handleChange('concepto')}
                  label="Concepto del Gasto *"
                >
                  {conceptos.map((c) => (
                    <MenuItem key={c.value} value={c.value}>
                      <Box display="flex" alignItems="center" gap={2}>
                        {c.icon}
                        <Typography>{c.label}</Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
                {errors.concepto && (
                  <Typography variant="caption" color="error">
                    {errors.concepto}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            {/* Monto */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Monto *"
                type="number"
                value={formData.monto}
                onChange={handleChange('monto')}
                error={!!errors.monto}
                helperText={errors.monto || 'Ingrese el monto del gasto'}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">Gs.</InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* Comprobante */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Número de Comprobante (opcional)"
                value={formData.comprobante}
                onChange={handleChange('comprobante')}
                helperText="Ej: FC-12345, Ticket-987"
                placeholder="FC-12345"
              />
            </Grid>

            {/* Descripción */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Descripción (opcional)"
                multiline
                rows={3}
                value={formData.descripcion}
                onChange={handleChange('descripcion')}
                helperText="Detalles adicionales del gasto"
                placeholder="Ej: Gasoil - Estación Esso Ruta 2, Km 45"
              />
            </Grid>

          </Grid>
        </CardContent>
      </Card>

      {/* Vista Previa */}
      {formData.monto && parseFloat(formData.monto) > 0 && (
        <Card sx={{ mt: 3, border: '2px solid #FF5722' }}>
          <CardContent>
            <Typography variant="h6" fontWeight="bold" gutterBottom textAlign="center">
              Resumen del Gasto
            </Typography>
            
            <Box sx={{ mt: 2 }}>
              {formData.concepto && (
                <Box display="flex" justifyContent="space-between" py={1}>
                  <Typography color="text.secondary">Concepto:</Typography>
                  <Typography fontWeight="600">
                    {conceptos.find(c => c.value === formData.concepto)?.label}
                  </Typography>
                </Box>
              )}
              
              <Box display="flex" justifyContent="space-between" py={1}>
                <Typography color="text.secondary">Monto:</Typography>
                <Typography variant="h5" fontWeight="bold" color="error">
                  Gs. {formatMoney(formData.monto)}
                </Typography>
              </Box>

              {formData.comprobante && (
                <Box display="flex" justifyContent="space-between" py={1}>
                  <Typography color="text.secondary">Comprobante:</Typography>
                  <Typography fontWeight="600">{formData.comprobante}</Typography>
                </Box>
              )}

              {formData.descripcion && (
                <Box py={1}>
                  <Typography color="text.secondary" gutterBottom>
                    Descripción:
                  </Typography>
                  <Typography fontWeight="600">{formData.descripcion}</Typography>
                </Box>
              )}

              <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #E0E0E0' }}>
                <Typography variant="body2" color="text.secondary">
                  {new Date().toLocaleString('es-PY')}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Botones de Acción */}
      <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
        <Button
          variant="outlined"
          size="large"
          onClick={() => navigate('/chofer/dashboard')}
          disabled={submitting}
          sx={{ flex: 1 }}
        >
          Cancelar
        </Button>
        <Button
          variant="contained"
          size="large"
          startIcon={submitting ? <CircularProgress size={20} /> : <OtherIcon />}
          onClick={handleSubmit}
          disabled={submitting}
          sx={{
            flex: 2,
            bgcolor: '#FF5722',
            '&:hover': { bgcolor: '#e64a19' },
            fontSize: '1.1rem',
            py: 2,
          }}
        >
          {submitting ? 'Registrando...' : 'REGISTRAR GASTO'}
        </Button>
      </Box>
    </Box>
  );
};

export default NuevoGastoChofer;

// cpalacios01@gmail.com | +595994648273
